import { useState } from "react";
import { queryKeys } from "@/api/queryKeys";
import { GoogleLogin, CredentialResponse } from "@react-oauth/google";
import { useQuery } from "@tanstack/react-query";
import api from "@/api/client";
import { fetchLoginUrl } from "@/api/portfolioApi";
import { RefreshCw } from "lucide-react";

interface Props {
  onSuccess: () => void;
}

export default function LoginPage({ onSuccess }: Props) {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { data: loginData } = useQuery({ queryKey: queryKeys.loginUrl, queryFn: fetchLoginUrl });

  async function handleCredential(response: CredentialResponse) {
    if (!response.credential) {
      setError("No credential received from Google.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await api
        .post<{ token: string; email: string }>("/auth/google-signin", {
          credential: response.credential,
        })
        .then((r) => r.data);
      localStorage.setItem("mct_token", data.token);
      onSuccess();
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { detail?: string } }; message?: string };
      const msg =
        e?.response?.data?.detail
        ?? (e?.response?.status ? `Backend error ${e.response.status}` : null)
        ?? e?.message
        ?? "Sign-in failed — check backend is running on port 8000.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex h-screen items-center justify-center bg-bg relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 flex items-end gap-1 opacity-20">
        {Array.from({ length: 20 }).map((_, i) => (
          <div key={i} className={`w-3 rounded-t-sm ${i % 3 === 0 ? "bg-buy" : i % 3 === 1 ? "bg-sell" : "bg-brand"}`}
            style={{ height: `${20 + Math.random() * 60}px` }} />
        ))}
      </div>

      <div className="w-full max-w-sm rounded-2xl border border-border bg-surface p-10 shadow-lg text-center relative z-10">
        {/* Logo */}
        <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-brand/15 border border-brand/25 flex items-center justify-center">
          <img src="/mct-logo.png" alt="MCT" className="h-8 w-8 object-contain" />
        </div>
        <h1 className="text-2xl font-black text-white mb-1">MCT</h1>
        <p className="text-[10px] text-zinc-600 uppercase tracking-wider mb-8">Algorithmic Core V2.4</p>

        {/* System Gateway */}
        <h2 className="text-base font-bold text-white mb-1">System Gateway</h2>
        <p className="text-xs text-zinc-500 mb-6">Access your high-frequency trading dashboard</p>

        {/* Google Login */}
        {loading ? (
          <p className="text-sm text-zinc-500">Verifying...</p>
        ) : (
          <div className="flex justify-center">
            <GoogleLogin
              onSuccess={handleCredential}
              onError={() => setError("Google sign-in popup failed.")}
              useOneTap={false}
              theme="filled_black"
              shape="pill"
              text="signin_with"
            />
          </div>
        )}

        {/* Zerodha note + Refresh Token */}
        <div className="mt-6 surface text-left">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-buy" />
              <span className="text-white text-xs font-semibold">Secure OAuth Bridge</span>
            </div>
            {loginData?.url && (
              <a
                href={loginData.url}
                className="flex items-center gap-1 text-[10px] font-semibold text-brand hover:text-indigo-400 transition"
              >
                <RefreshCw size={10} />
                Refresh Kite Token
              </a>
            )}
          </div>
          <p className="text-zinc-600 text-[10px]">
            After login, you'll connect to Zerodha Kite to enable algorithmic trading. Kite token expires daily at 06:00 AM IST — re-login required each morning.
          </p>
        </div>

        {error && (
          <p className="mt-4 text-sm text-red-400 bg-red-900/20 rounded-lg px-4 py-3">
            {error}
          </p>
        )}

        <p className="mt-6 text-[10px] text-zinc-800 leading-relaxed">
          By accessing MCT Core, you agree to the algorithmic trading disclaimers. Unauthorised access is strictly prohibited.
        </p>
      </div>
    </div>
  );
}
